import { client as gqlClient } from '@/api/GraphQLClient';

import { ApolloProvider, gql, useSubscription } from '@apollo/client';
import {
  Button,
  Grid,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEventHandler, useCallback, useEffect, useState } from 'react';
import { PauseCircle, PlayCircle } from '@mui/icons-material';
import MuiTable from '@mui/material/Table';
import MuiTableBody from '@mui/material/TableBody';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableContainer from '@mui/material/TableContainer';
import MuiTableRow from '@mui/material/TableRow';

const MAX_HISTORY_LENGTH = 1000;
const DEFAULT_FILTER_TEXT = '({ payload, topic }) => payload';
const EXAMPLE_FILTERS: Record<string, string> = {
  'Show all': DEFAULT_FILTER_TEXT,
  'Topic starts with "thread"':
    '({ payload, topic }) => topic.startsWith("thread:")',
  'Specific thread':
    '({ payload, topic }) => topic.includes("c0678f17-6561-4061-94bc-b117a794ad28")',
  'All "message_posted"':
    '({ payload, topic }) => topic.endsWith(":message_posted")',
  'Local Env':
    '({ payload, topic }) => payload.debug?.env === "local" || topic.startsWith("local-")',
};

type Payload = Record<string, any>;
type PayloadHistory = {
  index: number;
  cursor: string | null;
  topic: string | null;
  payload: Payload;
};
type Filter = (msg: PayloadHistory) => boolean;

let nextIndex = 0;

export const PubSubDebugPage = () => {
  return (
    <MainContainer>
      <ApolloProvider client={gqlClient}>
        <Page />
      </ApolloProvider>
    </MainContainer>
  );
};

export const Page = () => {
  const [filterText, setFilterText] = useState(DEFAULT_FILTER_TEXT);
  const [filter, setFilter] = useState<{ fn: Filter }>({
    fn: mkFilter(DEFAULT_FILTER_TEXT),
  });
  const [payloadHistory, setPayloadHistory] = useState<PayloadHistory[]>([]);
  const [isStreaming, setIsStreaming] = useState(true);

  const isFilterTextInExamples =
    Object.values(EXAMPLE_FILTERS).includes(filterText);

  const updateFilterText = useCallback(
    (newFilterText: string) => {
      setFilterText(newFilterText);
      try {
        const newFilter = mkFilter(newFilterText);
        setFilter({ fn: newFilter });
      } catch (e) {
        /* do nothing */
      }
    },
    [setFilterText, setFilter],
  );

  const onChangeFilterText: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => updateFilterText(event.target.value),
    [updateFilterText],
  );

  // HACK: Typing is not working for SelectChangeEvent
  const onChangeFilterExample = useCallback(
    (event: any) => updateFilterText(event.target.value),
    [updateFilterText],
  );

  const onClickPlay = useCallback(() => {
    setIsStreaming(true);
  }, [setIsStreaming]);

  const onClickPause = useCallback(() => {
    setIsStreaming(false);
  }, [setIsStreaming]);

  // Subscribe to new PubSub messages, add to history
  const { data, error } = useSubscription(ON_PUBLISH_SUBSCRIPTION, {
    skip: !isStreaming,
  });
  useEffect(() => {
    if (!data) {
      return;
    }
    console.log({ data });
    const payload: Payload = JSON.parse(data?.listen?.payloadJSON ?? '{}');
    const newPayloadHistory: PayloadHistory = {
      index: nextIndex++,
      cursor: data.listen?.cursor ?? null,
      topic: data.listen?.topic ?? null,
      payload,
    };
    setPayloadHistory((history) => [
      newPayloadHistory,
      ...history.slice(0, MAX_HISTORY_LENGTH),
    ]);
  }, [data]);

  // Filter the history based on the filter function
  let filteredHistory = payloadHistory;
  try {
    filteredHistory = payloadHistory.filter((msg) => filter.fn(msg));
  } catch (e) {
    console.error(e);
  }

  return (
    <div>
      {error && <Typography color="error">{error.message}</Typography>}
      <Grid container>
        <Grid item xs={2}>
          <Typography variant="h4">Pub/Sub Debug</Typography>
        </Grid>
        <Grid item xs={1}>
          {isStreaming ? (
            <Button
              key="pause"
              onClick={onClickPause}
              startIcon={<PauseCircle />}
            >
              Pause
            </Button>
          ) : (
            <Button key="play" onClick={onClickPlay} startIcon={<PlayCircle />}>
              Resume
            </Button>
          )}
        </Grid>
        <Grid item xs={6}>
          <TextField
            multiline
            fullWidth
            value={filterText}
            onChange={onChangeFilterText}
          />
        </Grid>
        <Grid item xs={3}>
          <Select
            value={isFilterTextInExamples ? filterText : ''}
            onChange={onChangeFilterExample}
          >
            {Object.entries(EXAMPLE_FILTERS).map(([label, value]) => (
              <MenuItem key={label} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <MuiTableContainer sx={{ overflow: 'initial' }}>
        <MuiTable stickyHeader sx={{ minWidth: '100%' }} size="small">
          <MuiTableRow>
            <TableRowHeader>Cursor</TableRowHeader>
            <TableRowHeader>Topic</TableRowHeader>
            <TableRowHeader>Payload</TableRowHeader>
          </MuiTableRow>
          <MuiTableBody>
            {filteredHistory.map(({ cursor, topic, payload, index }) => (
              <MuiTableRow
                key={index}
                sx={{ bgcolor: index % 2 ? '#ddd' : '#eee' }}
              >
                <MuiTableCell>{cursor}</MuiTableCell>
                <MuiTableCell sx={{ maxWidth: '200px' }}>{topic}</MuiTableCell>
                <MuiTableCell>
                  <pre>{JSON.stringify(payload, null, 2)}</pre>
                </MuiTableCell>
              </MuiTableRow>
            ))}
          </MuiTableBody>
        </MuiTable>
      </MuiTableContainer>
    </div>
  );
};

const MainContainer = styled('main')`
  height: 100vh;
  padding: ${({ theme }) => theme.spacing(3)};
  width: calc(100% - 65px);
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const TableRowHeader = styled(MuiTableCell)`
  background-color: ${({ theme }) => theme.color.N1.hex};
  clip-path: inset(0px -15px 0px 0px);
  filter: drop-shadow(5px 0px 4px ${({ theme }) => theme.color.N4.hex});
  font-weight: 700;
  left: 0;
  position: sticky;
  z-index: 1;
`;

const ON_PUBLISH_SUBSCRIPTION = gql`
  subscription OnPublishDebug {
    listen(topic: "*", after: "0-0") {
      cursor
      topic
      payloadJSON
    }
  }
`;

export function mkFilter(filterText: string): Filter {
  if (!filterText || !filterText.trim()) {
    return () => true;
  }
  try {
    // eslint-disable-next-line no-new-func
    return new Function('msg', `return !!(${filterText})(msg);`) as Filter;
  } catch (e) {
    return () => false;
  }
}
